import { Renderer, utils } from "@pixi/core";
import Big from "big.js";
import { VirtualScreenEvent } from "by/gamefactory/analytics/events/VirtualScreenEvent";
import { NotificationLayerProvider } from "by/gamefactory/cityversetycoon/composition/providers/NotificationLayerProvider";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";
import { TickerDelay } from "by/gamefactory/tools/TickerDelay";
import { VariableSaver } from "by/gamefactory/tools/variableSaver/VariableSaver";
import { gsap } from "gsap";
import { IDestroyOptions, Point, Sprite, Texture } from "pixi.js";
import { Camera, Color, Cubemap, ImageBasedLighting, Light, LightType, LightingEnvironment, Quaternion, ShadowCastingLight, ShadowQuality } from "pixi3d/pixi7";
import { IAnalyticProviderWrapper } from "../../../../analytics/IAnalyticProviderWrapper";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../shared/async/PromiseCompletionSource";
import { nameof } from "../../../../shared/bindable-data";
import { PersonalOfferReason } from "../../../networking/data/responses/personaloffer/PersonalOfferReason";
import { BuildingRightsType } from "../../../networking/data/responses/user/BuildingRightsType";
import { SceneIndex } from "../../SceneIndex";
import { BaseScalableView } from "../../common/BaseScalableView";
import { AnimationTargetProvider } from "../../common/components/AnimationTargetProvider";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { Hand } from "../../common/components/Hand";
import { HandWithDelay } from "../../common/components/HandWithDelay";
import { RollProgressCounter } from "../../common/components/RollProgressCounter";
import { IApplicationModel } from "../../common/models/IApplicationModel";
import { IFirstTimeExperienceModel } from "../../common/models/IFirstTimeExperienceModel";
import { IGuestModel } from "../../common/models/IGuestModel";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { IRenterPolicyModel } from "../../common/models/Renters/IRenterPolicyModel";
import { ILatestNewsModel } from "../../common/models/latestnews/ILatestNewsModel";
import { LatestNewsType } from "../../common/models/latestnews/LatestNewsType";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";
import { MusicResourcePackageWrapper } from "../../common/resources/MusicResourcePackageWrapper";
import { SoundsResourcePackageWrapper } from "../../common/resources/SoundsResourcePackageWrapper";
import { HourlyBonusState } from "../../popups/hourlyBonus/mainHourlyBonus/HourlyBonusState";
import { LobbyDiceCollectAnimation } from "../../popups/hourlyBonus/mainHourlyBonus/components/LobbyDiceCollectAnimation";
import { IFortuneWheelModel } from "../../popups/hourlyBonus/model/IFortuneWheelModel";
import { NotificationMessageType } from "../../popups/notificationMessages/NotificationMessageType";
import { CurrentPiggyState } from "../../popups/piggy/components/piggycontentview/CurrentPiggyContentView";
import { RenterPolicyType } from "../../popups/renterpolicy/components/RenterPolicyType";
import { UserProfileResourcePackage } from "../../popups/userprofile/resources/UserProfileResourcePackage";
import { CityAnimationContainer } from "../components/CityAnimationContainer";
import { FadeScreen } from "../components/FadeScreen";
import { HourlyBonusButton } from "../components/HourlyBonusButton";
import { InputBlocker } from "../components/InputBlocker";
import { LobbyFeaturePanel } from "../components/LobbyFeaturePanel";
import { MarketplaceButton } from "../components/MarketplaceButton";
import { MyBuildingsButton } from "../components/MyBuildingsButton";
import { NewsButton } from "../components/NewsButton";
import { PiggyButton } from "../components/PiggyButton";
import { RenterButton } from "../components/RenterButton";
import { RollButton } from "../components/RollButton";
import { AnimatedDices } from "../components/dices/AnimatedDices";
import { DefaultDiceAnimationIndexProvider } from "../components/dices/animationIndexProviders/impl/DefaultDiceAnimationIndexProvider";
import { NewsPresenter } from "../components/news/NewsPresenter";
import { TopPanel } from "../components/toppanel/TopPanel";
import { IGameController } from "../controllers/IGameController";
import { DiceRollAction } from "../flow/impl/DiceRollAction";
import { ScreenAction } from "../flow/impl/ScreenAction";
import { DialogAction } from "../flow/impl/dialogAction/DialogAction";
import { GiftPiggyWheelAction } from "../flow/impl/diceActions/GiftPiggyWheelAction";
import { RaidNewsCard } from "../flow/impl/news/RaidNewsCard";
import { VisitNewsCard } from "../flow/impl/news/VisitNewsCard";
import { IGameModel } from "../models/IGameModel";
import { IGameViewModel } from "../models/IGameViewModel";
import { INewsModel } from "../models/INewsModel";
import { GameResourcePackage } from "../resources/GameResourcePackage";
import { LobbySpineResourcePackage } from "../resources/LobbySpineResourcePackage";
import { NewsResourcePackage } from "../resources/NewsResourcePackage";
import { WalletTabType } from "../../popups/myWallet/components/WalletTabType";
import { TournamentButton } from "../../common/components/TournamentButton";
import { TooltipsInfo } from "../../common/models/TooltipsInfo";
import { GameInfo, Stage } from "../../../../analytics/GameInfo";
import { FullscreenInputBlocker } from "../components/FullscreenInputBlocker";
import { IFortuneWheelController } from "../../popups/hourlyBonus/mainHourlyBonus/controller/IFortuneWheelController";
import { ITournamentModel } from "../../common/models/tournament/ITournamentModel";
import { TournamentState } from "../../common/models/tournament/TournamentState";
type Timeline = gsap.core.Timeline;

export class GameView extends BaseScalableView<void> {
    private static readonly ZOOM_OUT_DELAY = 7;

    private readonly commonResources: CommonResourcesPackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly gameResources: GameResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly musicResources: MusicResourcePackageWrapper;
    private readonly lobbySpineResources: LobbySpineResourcePackage;
    private readonly variableSaver: VariableSaver;
    private readonly playerModel: IPlayerModel;
    private readonly guestModel: IGuestModel;
    private readonly gameModel: IGameModel;
    private readonly gameViewModel: IGameViewModel;
    private readonly gameController: IGameController;
    private readonly fortuneWheelController: IFortuneWheelController;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly renderer: Renderer;
    private readonly notificationLayerProvider: NotificationLayerProvider;
    private readonly applicationModel: IApplicationModel;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly connectionModel: IConnectionModel;
    private readonly logger: ILogger;
    private readonly renterPolicyModel: IRenterPolicyModel;
    private readonly latestNewsModel: ILatestNewsModel;
    private readonly tournamentModel: ITournamentModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly gameInfo: GameInfo;

    private background: Sprite;
    private tempBackground: Sprite;
    private shadowCastingLight: ShadowCastingLight;
    private topPanel: TopPanel;
    private rollProgress: RollProgressCounter;
    private rollButton: RollButton;
    private myBuildingsFadeScreen: FadeScreen;
    //TODO: seems not use anymore
    private diceCollectAnimation: LobbyDiceCollectAnimation;
    private collectAnimationPoint: Point;
    private myBuildingsButton: MyBuildingsButton;
    private marketplaceButton: MarketplaceButton;
    private cityAnimation: CityAnimationContainer;

    private rightPanel: LobbyFeaturePanel;
    private hourlyBonusButton: HourlyBonusButton;
    private renterButton: RenterButton;
    private tournamentButton: TournamentButton;

    private leftPanel: LobbyFeaturePanel;
    private newsButton: NewsButton;
    private piggyButton: PiggyButton;

    private hand: HandWithDelay;
    private myBuildingHand: Hand;
    private bottomPanel: Sprite;
    private animatedDices: AnimatedDices;
    private inputBlocker: InputBlocker;
    private newsPresenter: NewsPresenter;
    private newsModel: INewsModel;
    private newsResources: NewsResourcePackage;
    private zoomOutTween: Timeline;
    private throwDiceFlag: boolean = false;

    private showComponentsAnimation: gsap.core.Timeline;

    public constructor(
        commonResources: CommonResourcesPackage,
        gameResources: GameResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        musicResources: MusicResourcePackageWrapper,
        newsResources: NewsResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        variableSaver: VariableSaver,
        playerModel: IPlayerModel,
        guestModel: IGuestModel,
        applicationModel: IApplicationModel,
        gameModel: IGameModel,
        gameViewModel: IGameViewModel,
        newsModel: INewsModel,
        gameController: IGameController,
        fortuneWheelController: IFortuneWheelController,
        fortuneWheelModel: IFortuneWheelModel,
        canvasScaler: ICanvasScaler,
        analyticsProvider: IAnalyticProviderWrapper,
        operationControls: PromiseCompletionSource<void>,
        renderer: Renderer,
        notificationLayerProvider: NotificationLayerProvider,
        topPanel: TopPanel,
        firstTimeExperienceModel: IFirstTimeExperienceModel,
        connectionModel: IConnectionModel,
        logger: ILogger,
        renterPolicyModel: IRenterPolicyModel,
        latestNewsModel: ILatestNewsModel,
        tournamentModel: ITournamentModel,
        asyncOperationNotification: AsyncOperationNotification,
        gameInfo: GameInfo
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.gameResources = gameResources;
        this.soundResources = soundResources;
        this.musicResources = musicResources;
        this.newsResources = newsResources;
        this.userProfileResources = userProfileResources;
        this.lobbySpineResources = new LobbySpineResourcePackage();
        this.variableSaver = variableSaver;
        this.playerModel = playerModel;
        this.guestModel = guestModel;
        this.applicationModel = applicationModel;
        this.gameModel = gameModel;
        this.gameViewModel = gameViewModel;
        this.newsModel = newsModel;
        this.gameController = gameController;
        this.fortuneWheelController = fortuneWheelController;
        this.fortuneWheelModel = fortuneWheelModel;
        this.analyticsProvider = analyticsProvider;
        this.renderer = renderer;
        this.notificationLayerProvider = notificationLayerProvider;
        this.topPanel = topPanel;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.connectionModel = connectionModel;
        this.logger = logger;
        this.renterPolicyModel = renterPolicyModel;
        this.latestNewsModel = latestNewsModel;
        this.tournamentModel = tournamentModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.gameInfo = gameInfo;
        this.showComponentsAnimation = new gsap.core.Timeline();
        this.setupStage3d();

        this.gameModel.propertyChanged.add(this.onGameModelPropertyChanged, this);
        this.playerModel.propertyChanged.add(this.onPlayerModelPropertyChanged, this);
        this.tournamentModel.propertyChanged.add(this.onTournamentModelPropertyChanged, this);
        this.renterPolicyModel.propertyChanged.add(this.onRenterPolicyModelPropertyChanged, this);
        this.firstTimeExperienceModel.propertyChanged.add(this.onFirstTimeExperienceModelChanged, this);
        this.firstTimeExperienceModel.tooltipsInfo.value.propertyChanged.add(this.onFirstTimeExperienceTooltipsChanged, this);
        this.connectionModel.propertyChanged.add(this.onConnectionModelPropertyChange, this);

        this.build();

        this.background.on("pointerdown", this.gameController.userActive, this.gameController);
        this.tempBackground.on("pointerdown", this.gameController.userActive, this.gameController);
        this.rollButton.onUp.add(this.onRollClick, this);
        this.diceCollectAnimation.fakeDiceCollected.add(this.rollProgress.incrementProgress, this.rollProgress);
        this.renterButton.onButtonClick.add(this.onRenterInfoButtonClick, this);
        this.tournamentButton.onButtonClick.add(this.onTournamentButtonClick, this);
        this.marketplaceButton.onUp.add(this.marketplaceClickHandle, this);
        this.myBuildingsButton.onUp.add(this.myBuildingsClickHandle, this);
        this.hourlyBonusButton.onStyleChanged.add(this.rightPanel.alignmentStrategy, this.rightPanel);
        this.hourlyBonusButton.onClaimHourlyBonusRewardClick.add(this.onClaimHourlyBonusRewardClick, this);
        this.hourlyBonusButton.onHourlyBonusSceneRequested.add(this.onHourlyBonusSceneRequested, this);
        this.fortuneWheelModel.propertyChanged.add(this.onFortuneWheelPropertyChanged, this);
        this.latestNewsModel.propertyChanged.add(this.onLatestNewsModelPropertyChange, this);

        this.layout(canvasScaler.referenceWidth, canvasScaler.referenceHeight);
        this.once("added", this.showView.bind(this));
    }

    public async start(): Promise<void> {
        this.musicResources.assets_music_CityVerse_BG_Music_02_mp3.play();
        this.floatingTopPanel();
        this.loadAnimatedBackground();
    }

    private async onGameModelPropertyChanged(model: IGameModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "action"):
                const action = model.action.value;
                if (action === null) {
                    return;
                }
                switch (action.constructor) {
                    case DiceRollAction:
                        const diceAction = action as DiceRollAction;
                        if (diceAction.diceComboAction.constructor == GiftPiggyWheelAction) {
                            await this.fortuneWheelModel.piggyWheelModel.value.loadPiggyWheelStatus();
                            if (new Big(this.playerModel.tokens.value ?? 0).lt(this.fortuneWheelModel.piggyWheelModel.value.priceInTokens.value)) {
                                await this.gameController.claimAction();
                            }
                        }
                        break;
                    case DialogAction:
                        const dialogAction = action as DialogAction;
                        await this.dialogActionHandle(dialogAction);
                        break;
                    case ScreenAction:
                        const screenAction = action as ScreenAction;
                        await this.screenActionHandle(screenAction);
                }
                break;
        }
    }

    private async onTournamentModelPropertyChanged(model: ITournamentModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "state"):
                const days = model.time.value / TournamentButton.MILLISECONDS_IN_DAY;
                this.tournamentButton.visible = model.state.value !== TournamentState.UNAVAILABLE && !(model.state.value == TournamentState.FINISHED && days >= TournamentButton.INTERACTIVE_BUTTON_IN_FINISHED_DAY);
                this.layoutFeaturePanels(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
                break;
        }
    }

    private async onRenterPolicyModelPropertyChanged(model: IRenterPolicyModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "penalties"):
                if (model.penalties.value != undefined) {
                    await this.operationAfterAllPopupsClose(
                        () => this.gameController.showRenterPolicyPopup(RenterPolicyType.NOTIFICATION_DEFAULT),
                        () => model.penalties.value != undefined
                    );
                }
                break;
        }
    }

    private async onFirstTimeExperienceModelChanged(model: IFirstTimeExperienceModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "becameOwner"):
                if (model.becameOwner.value) {
                    await this.operationAfterAllPopupsClose(
                        async () => {
                            await this.asyncOperationNotification.autoShowHide(this.playerModel.loadBuildings(false));
                            await this.gameController.showOwnerNotificationPopup(NotificationMessageType.UNBOARDING_OWNER);
                        },
                        () => model.becameOwner.value
                    );
                }
                break;
            case nameof(model, "piggyIconInLobby"):
                this.piggyButton.visible = model.piggyIconInLobby.value;
                this.layoutFeaturePanels(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
                if (model.tooltipsInfo.value?.piggyIntro) {
                    const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
                    this.addChild(inputBlocker);
                    await this.operationAfterAllPopupsClose(
                        () => this.inputBlockerOperation(this.gameController.showPopupBySceneIndex(SceneIndex.CHECKOUT_PIGGY)),
                        () => model.tooltipsInfo.value?.piggyIntro
                    );
                    this.removeChild(inputBlocker);
                }
                break;
        }
    }

    private async onFirstTimeExperienceTooltipsChanged(tooltipsInfo: TooltipsInfo, key: string): Promise<void> {
        switch (key) {
            case nameof(tooltipsInfo, "renterQuestIntro"):
                this.renterButton.visible = this.playerModel.buildingRights.value === BuildingRightsType.RENTER && !this.firstTimeExperienceModel.tooltipsInfo.value?.renterQuestIntro.value;
                this.layoutFeaturePanels(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
                break;
        }
    }

    private async operationAfterAllPopupsClose<TResult>(operation: () => Promise<TResult>, executeCondition: () => boolean): Promise<TResult> {
        if (this.gameViewModel.popupsShown.value === 0) {
            if (executeCondition()) {
                return await operation();
            }
            return null;
        }
        const pcs = new PromiseCompletionSource<TResult>();

        const connectionAbortHandler = async (connectionModel: IConnectionModel, propertyName: string) => {
            if (propertyName === nameof(connectionModel, "connected") && !connectionModel.connected.value) {
                this.gameViewModel.propertyChanged.remove(popupsHideAwaiterer);
                this.connectionModel.propertyChanged.remove(connectionAbortHandler);
                pcs.complete(null);
            }
        }

        const popupsHideAwaiterer = async (gameViewModel: IGameViewModel, propertyName: string) => {
            if (propertyName === nameof(gameViewModel, "popupsShown") && gameViewModel.popupsShown.value === 0) {
                this.gameViewModel.propertyChanged.remove(popupsHideAwaiterer);
                this.connectionModel.propertyChanged.remove(connectionAbortHandler);
                try {
                    if (executeCondition()) {
                        pcs.complete(await operation());
                        return;
                    }
                    pcs.complete(null);
                }
                catch (e: unknown) {
                    this.logger.error("Exception on process operation after all popups close: ", e);
                    pcs.complete(null);
                }
            }
        };
        this.connectionModel.propertyChanged.add(connectionAbortHandler);
        this.gameViewModel.propertyChanged.add(popupsHideAwaiterer);
        return await pcs.result;
    }

    private async onConnectionModelPropertyChange(model: IConnectionModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    await this.gameController.reconnect();
                }
                break;
        }
    }

    private async onLatestNewsModelPropertyChange(model: ILatestNewsModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "latestNewsStatus"):
                if (this.firstTimeExperienceModel.tooltipsInfo.value?.ownerWithoutBuildings && model.latestNewsStatus.value.hasUnread && this.playerModel.buildingRights.value === BuildingRightsType.OWNER) {
                    await model.getLatestNews();
                    if (model.latestNews.value.items?.some((newsItem) => newsItem?.type === LatestNewsType.HOLDER_SOLD_BUILDING)) {
                        await this.playerModel.loadBuildings(false);
                        if (this.playerModel.buildings.value.length > 0) {
                            return;
                        }
                        await this.operationAfterAllPopupsClose(() => {
                            return this.gameController.showOwnerNotificationPopup(NotificationMessageType.YOU_NO_MORE_OWNER);
                        }, () => this.firstTimeExperienceModel.tooltipsInfo.value?.ownerWithoutBuildings && this.playerModel.buildingRights.value === BuildingRightsType.OWNER && this.playerModel.buildings.value.length === 0);
                    }
                }
                if (this.playerModel.buildingRights.value === BuildingRightsType.RENTER && model.latestNewsStatus.value.hasUnread) {
                    await model.getLatestNews();
                    if (model.latestNews.value.items?.some((newsItem) => newsItem?.type === LatestNewsType.SUCCESSFUL_BUILDING_TRANSFER)) {
                        location.reload();
                    }
                }
                break;
        }
    }

    private async dialogActionHandle(dialogAction: DialogAction): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        try {
            this.addChild(inputBlocker);
            if (dialogAction.sceneIndex === SceneIndex.TUTORIAL) {
                this.topPanel.visible = false;
                this.rollButton.visible = false;
                this.rollProgress.visible = false;
            }
            await this.gameController.showPopupByDialogAction(dialogAction.sceneIndex, dialogAction.dialogData);
            if (dialogAction.sceneIndex === SceneIndex.TUTORIAL) {
                await this.onRollClick();
            }
        } finally {
            this.removeChild(inputBlocker);
        }
    }

    private async screenActionHandle(screenAction: ScreenAction): Promise<void> {
        try {
            this.inputBlocker.blocked = true;
            this.rollButton.interactive = false;
            if (screenAction.sceneIndex === SceneIndex.MY_BUILDINGS || screenAction.sceneIndex === SceneIndex.UPGRADE) {
                await this.gameController.claimAction();
                await this.playerModel.loadBuildings();
                await this.showMyBuildingButton();
            }
            if (screenAction.sceneIndex === SceneIndex.AUTH) {
                this.gameViewModel.mainLoginShown.setValue(true);
                await this.gameController.showMainLoginPopup();
                this.gameViewModel.mainLoginShown.setValue(false);
                await this.gameController.claimAction();
            }
        } finally {
            this.rollButton.interactive = true;
            this.inputBlocker.blocked = false;
        }
    }

    private async onPlayerModelPropertyChanged(model: IPlayerModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "rolls"):
                await this.diceRollsChangeHandle(model.rolls.value);
                break;
            case nameof(model, "buildingRights"):
                this.renterButton.visible = this.playerModel.buildingRights.value === BuildingRightsType.RENTER && !this.firstTimeExperienceModel.tooltipsInfo.value?.renterQuestIntro.value;
                this.layoutFeaturePanels(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
                break;
        }
    }

    private async diceRollsChangeHandle(rolls: number): Promise<void> {
        await this.rollProgress.setProgress(rolls);
    }

    private build(): void {
        this.background = new Sprite(this.gameResources.assets_views_game_ui_background_png);
        this.background.eventMode = 'auto';
        this.addChild(this.background);

        this.tempBackground = new Sprite(this.gameResources.assets_views_game_staticBackground_png);
        this.tempBackground.eventMode = 'auto';
        this.addChild(this.tempBackground);

        this.animatedDices = new AnimatedDices(
            this.gameResources.assets_views_game_dice_AnimatedDices_gltf,
            this.renderer,
            this.shadowCastingLight,
            new DefaultDiceAnimationIndexProvider(9) // Amount of animations now imported to AnimatedDices asset. ToDo: rewise this step.
        );
        this.addChild(this.animatedDices);

        this.leftPanel = new LobbyFeaturePanel(18.5);
        this.addChild(this.leftPanel);

        this.newsButton = new NewsButton(this.commonResources, this.gameResources, this.soundResources, this.gameController, this.latestNewsModel, this.logger);
        this.piggyButton = new PiggyButton(this.commonResources, this.soundResources, this.gameController, this.firstTimeExperienceModel, this.logger);
        this.leftPanel.addChild(this.piggyButton, this.newsButton);

        this.bottomPanel = new Sprite(this.gameResources.assets_views_game_ui_union_png);
        this.bottomPanel.anchor.x = 0.5;
        this.addChild(this.bottomPanel);
        this.rollButton = new RollButton(this.gameResources, this.fortuneWheelModel, this.playerModel, this.soundResources.assets_sounds_Dice_Button_Click_mp3);
        this.addChild(this.rollButton);
        this.rollProgress = new RollProgressCounter(
            this.commonResources.assets_common_ui_progress_counter_bottom_progress_png,
            this.commonResources.assets_common_ui_progress_counter_yellow_progress_png,
            this.playerModel.rolls.value
        );
        this.addChild(this.rollProgress);
        this.collectAnimationPoint = new Point();
        this.diceCollectAnimation = new LobbyDiceCollectAnimation(
            [this.commonResources.assets_common_ui_dices_dice_01_png, this.commonResources.assets_common_ui_dices_dice_03_png],
            Texture.EMPTY,
            10,
            this.collectAnimationPoint,
            this.fortuneWheelModel
        );
        this.inputBlocker = new InputBlocker();
        this.inputBlocker.blocked = false;

        this.rightPanel = new LobbyFeaturePanel(18);
        this.addChild(this.rightPanel);

        this.hourlyBonusButton = new HourlyBonusButton(
            this.fortuneWheelModel,
            this.applicationModel,
            this.gameResources,
            this.variableSaver,
            this.guestModel
        );
        this.renterButton = new RenterButton(this.commonResources, this.gameResources, this.soundResources, this.renterPolicyModel);

        this.tournamentButton = new TournamentButton(this.gameResources, this.soundResources, this.tournamentModel);
        this.onTournamentModelPropertyChanged(this.tournamentModel, "state")

        this.rightPanel.addChild(this.hourlyBonusButton, this.renterButton, this.tournamentButton);

        this.hand = new HandWithDelay(this.gameViewModel, this.commonResources);
        this.hand.pivot.set(this.hand.width * 0.5, this.hand.height * 0.5);
        this.addChild(this.hand);
        this.marketplaceButton = new MarketplaceButton(this.gameResources, this.commonResources);
        this.addChild(this.topPanel);
        this.addChild(this.marketplaceButton);
        this.myBuildingsFadeScreen = new FadeScreen(0.3, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.myBuildingsFadeScreen);
        this.myBuildingsButton = new MyBuildingsButton(this.gameResources, this.commonResources, this.playerModel, this.guestModel, this.variableSaver);
        this.addChild(this.myBuildingsButton);
        this.addChild(this.diceCollectAnimation);
        const newsAnimationTargetProvider = new AnimationTargetProvider();
        newsAnimationTargetProvider.register(VisitNewsCard.name, this.topPanel.coinsTarget);
        newsAnimationTargetProvider.register(RaidNewsCard.name, this.topPanel.shieldTarget);
        this.newsPresenter = new NewsPresenter(
            this.newsModel,
            this.playerModel,
            this.newsResources,
            this.commonResources,
            this.userProfileResources,
            this.canvasScaler,
            newsAnimationTargetProvider,
            this.gameController,
            this.firstTimeExperienceModel,
            this.asyncOperationNotification
        );
        this.notificationLayerProvider.notificationLayer.addChild(this.newsPresenter);
        // This should always be the last child to add
        this.addChild(this.inputBlocker);
    }

    private onTournamentButtonClick(): void {
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Click on tournament lobby icon", this.analyticsProvider.currentEventIndex));
        this.gameController.showTournament();
    }

    private async onRenterInfoButtonClick(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.gameController.showRenterPolicyPopup(RenterPolicyType.RENTER_POLICY);
        this.removeChild(inputBlocker);
    }

    private async loadAnimatedBackground(): Promise<void> {
        await this.lobbySpineResources.load();
        this.cityAnimation = new CityAnimationContainer(this.lobbySpineResources, this.canvasScaler);
        this.cityAnimation.on("pointerdown", this.gameController.userActive, this.gameController);
        this.cityAnimation.eventMode = 'static';
        this.addChildAt(this.cityAnimation, 1);
        this.removeChild(this.tempBackground);
        this.cityAnimation?.position.set(this.canvasScaler.referenceWidth * 0.5, this.canvasScaler.referenceHeight * 0.5 - 33);
        if (!this.throwDiceFlag) {
            await this.cityAnimation.playBackward();
        }
    }

    private async showView(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.renterButton.visible = this.playerModel.buildingRights.value === BuildingRightsType.RENTER && !this.firstTimeExperienceModel.tooltipsInfo.value?.renterQuestIntro.value;
        this.hand.visible = false;
        await this.gameController.firstShowMainLoginPopup();
        await TickerDelay.delay(500);
        this.renterButton.visible = this.playerModel.buildingRights.value === BuildingRightsType.RENTER && !this.firstTimeExperienceModel.tooltipsInfo.value?.renterQuestIntro.value;
        this.layoutFeaturePanels(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        if (!this.variableSaver.exist(`cookies,${this.guestModel.id.value}`) && this.variableSaver.exist(`TUTORIAL,${this.guestModel.id.value}`)) {
            this.gameController.showPopupBySceneIndex(SceneIndex.COOKIES);
        }
        if (this.firstTimeExperienceModel.becameOwner.value && this.playerModel.buildingRights.value == BuildingRightsType.OWNER) {
            await this.gameController.showOwnerNotificationPopup(NotificationMessageType.UNBOARDING_OWNER);
            await this.gameController.showMyWalletPopup(WalletTabType.BUILDING);
        }
        if (!this.variableSaver.exist(`unboarding`) && this.playerModel.buildingRights.value == BuildingRightsType.RENTER) {
            await this.gameController.showOwnerNotificationPopup(NotificationMessageType.UNBOARDING_RENTER);
        }
        const currentSection = this.fortuneWheelModel.currentHourlyBonusSection.value;
        if (!isNaN(currentSection) && currentSection == 3 && this.firstTimeExperienceModel.tooltipsInfo.value?.hourlyBonusWheelIntro && this.fortuneWheelModel.spinAvailableIn.value == 0) {
            await this.gameController.showCheckoutHourlyBonusPopup(true);
        }
        this.onGameModelPropertyChanged(this.gameModel, nameof(this.gameModel, "action"));
        inputBlocker.blocked = false;
        this.removeChild(inputBlocker);
    }

    private async showComponents(): Promise<void> {
        this.zoomOutTween = new gsap.core.Timeline({ delay: GameView.ZOOM_OUT_DELAY, paused: true });
        this.showComponentsAnimation.set(this.rollButton, { visible: true });
        this.showComponentsAnimation.set(this.rollProgress, { visible: true });
        this.showComponentsAnimation.set(this.topPanel, { visible: true });
        this.showComponentsAnimation.fromTo(this.rollButton, { alpha: 0, y: 1390 }, { alpha: 1, y: 1340, duration: 0.3, ease: "power2.in" }, "<");
        this.showComponentsAnimation.fromTo(this.bottomPanel, { alpha: 0, y: this.bottomPanel.y }, { alpha: 1, y: this.bottomPanel.y - 50, duration: 0.3, ease: "power2.in" }, "<");
        this.showComponentsAnimation.fromTo(this.rollProgress, { alpha: 0, y: this.rollProgress.y }, { alpha: 1, y: this.rollProgress.y - 50, duration: 0.3, ease: "power2.in" }, "<50%");
        this.showComponentsAnimation.fromTo(this.myBuildingsButton, { alpha: 0, y: this.myBuildingsButton.y }, { alpha: 1, y: this.myBuildingsButton.y - 50, duration: 0.3, ease: "power2.in" }, "<");
        this.showComponentsAnimation.fromTo(this.marketplaceButton, { alpha: 0, y: this.myBuildingsButton.y }, { alpha: 1, y: this.myBuildingsButton.y - 50, duration: 0.3, ease: "power2.in" }, "<");
        this.showComponentsAnimation.to(this.rightPanel, { alpha: 1, duration: 0.3, ease: "power2.in" }, "<");
        this.showComponentsAnimation.to(this.leftPanel, { alpha: 1, duration: 0.3, ease: "power2.in" }, "<");
        await Promise.all([this.showComponentsAnimation.play(), this.topPanel.show()]);
        this.showComponentsAnimation.clear();
        this.layoutFeaturePanels(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        await this.playSequenceBackwardWithDelay();
    }

    private async hideComponents(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.hand.scale, { x: 0, y: 0, duration: 0.15, onComplete: () => this.hand.visible = false, ease: "power2.in" }, "<");
        animationTimeLine.to(this.rightPanel, { alpha: 0, duration: 0.3, ease: "power2.in" }, "<");
        animationTimeLine.to(this.leftPanel, { alpha: 0, duration: 0.3, ease: "power2.in" }, "<");
        animationTimeLine.fromTo(this.rollProgress, { alpha: 1, y: this.rollProgress.y }, { alpha: 0, y: this.rollProgress.y + 50, duration: 0.3, ease: "power2.in" }, "<40%");
        animationTimeLine.fromTo(this.myBuildingsButton, { alpha: 1, y: this.myBuildingsButton.y }, { alpha: 0, y: this.myBuildingsButton.y + 50, duration: 0.3, ease: "power2.in" }, "<");
        animationTimeLine.fromTo(this.marketplaceButton, { alpha: 1, y: this.myBuildingsButton.y }, { alpha: 0, y: this.myBuildingsButton.y + 50, duration: 0.3, ease: "power2.in" }, "<");
        animationTimeLine.fromTo(this.rollButton, { alpha: 1, y: 1340 }, { alpha: 0, y: 1390, duration: 0.3, ease: "power2.in" }, "<");
        animationTimeLine.fromTo(this.bottomPanel, { alpha: 1, y: this.bottomPanel.y }, { alpha: 0, y: this.bottomPanel.y + 50, duration: 0.3, ease: "power2.in" }, "<");
        animationTimeLine.then(() => animationTimeLine.kill());
        if (!this.throwDiceFlag) {
            this.cityAnimation?.playForward();
        }
        await Promise.all([animationTimeLine.play(), this.topPanel.hide()]);
    }

    private async playSequenceBackwardWithDelay(): Promise<void> {
        this.zoomOutTween?.add(async () => {
            this.throwDiceFlag = false;
            await this.cityAnimation?.playBackward();
        });
        await this.zoomOutTween.play();
    }

    public async showMyBuildingButton(): Promise<void> {
        this.myBuildingsButton.unLockButtonWithTutorial();
        this.myBuildingsFadeScreen.eventMode = "static";
        this.gameController.popupShown();
        this.myBuildingsButton.tutorialAvailable = true;
        this.myBuildingHand = new Hand(this.commonResources);
        this.myBuildingHand.pivot.set(this.myBuildingHand.width * 0.5, this.myBuildingHand.height * 0.5);
        this.myBuildingHand.position.set(this.myBuildingsButton.x + this.myBuildingsButton.width * 0.55, this.myBuildingsButton.y - this.myBuildingsButton.height * 0.6);
        this.myBuildingHand.angle = 0;
        this.addChild(this.myBuildingHand);
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.fromTo(
            this.myBuildingHand.scale,
            { x: 0, y: 0 },
            { x: 0.75, y: 0.75, duration: 0.4, ease: "back.out(2)" },
            "<");
        await animationTimeLine;
        animationTimeLine.kill();
    }

    public hideMyBuildingTutorial(): void {
        this.myBuildingsFadeScreen.eventMode = "none";
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.myBuildingHand.scale, { x: 0, y: 0, duration: 0.4, ease: "back.in(2)" });
        animationTimeLine.set(this.myBuildingHand, { visible: false });
        animationTimeLine.then(() => {
            animationTimeLine.kill();
            this.myBuildingHand?.destroy();
        });
        this.myBuildingsButton.tutorialAvailable = false;
    }

    private async myBuildingsClickHandle(): Promise<void> {
        this.logger.debug("[BUILDINGS WORKFLOW] Click on building button");
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        inputBlocker.blocked = true;
        this.addChild(inputBlocker);
        try {
            this.myBuildingsButton.enabled = false;
            this.soundResources.assets_sounds_Choose_Card_mp3.play();
            await this.asyncOperationNotification.autoShowHide(this.playerModel.loadBuildings());
            if (this.playerModel.buildings.value.length === 0) {
                await this.gameController.showOwnerNotificationPopup(NotificationMessageType.YOU_NO_MORE_OWNER);
                this.myBuildingsButton.enabled = true;
                inputBlocker.blocked = false;
                this.removeChild(inputBlocker);
                return;
            }
            if (this.myBuildingsButton.tutorialAvailable && this.myBuildingHand) {
                this.gameController.popupHidden();
                this.hideMyBuildingTutorial();
            }
            const result = await this.gameController.showMyBuildingsPopup();
            this.myBuildingsButton.updateNewBuildingsText();
            if (result) {
                await this.gameController.showPiggyPopup(CurrentPiggyState.UNLOCKED);
                await TickerDelay.delayCallback(() => this.marketplaceButton.showDot(), 2200);
                await this.gameController.showPiggyPopup(CurrentPiggyState.NEW_PIGGY);
            }
        } catch (error: unknown) {
            this.logger.error(error);
        }
        finally {
            this.asyncOperationNotification.hide();
            this.myBuildingsButton.enabled = true;
            inputBlocker.blocked = false;
            this.removeChild(inputBlocker);
        }
    }

    private async marketplaceClickHandle(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        inputBlocker.blocked = true;
        this.addChild(inputBlocker);
        try {
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Click on MP lobby icon", this.analyticsProvider.currentEventIndex));
            this.soundResources.assets_sounds_myBuildings_upgrade_home_button_click_mp3.play();
            await this.gameController.showMarketPlacePopupScene();
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            inputBlocker.blocked = false;
            this.removeChild(inputBlocker);
        }
    }

    private async onRollClick(): Promise<void> {
        this.inputBlocker.blocked = true;
        this.gameController.userActive();
        if (this.playerModel.buildingRights.value === BuildingRightsType.OWNER && this.playerModel.buildings.value.length === 0) {
            await this.gameController.showOwnerNotificationPopup(NotificationMessageType.YOU_NO_MORE_OWNER);
            this.inputBlocker.blocked = false;
            return;
        }
        if (await this.onDiceRollsOver()) {
            this.inputBlocker.blocked = false;
            return;
        }
        try {
            this.zoomOutTween?.kill();
            this.showComponentsAnimation?.progress(1);
            this.gameController.subtractRolls();
            await this.hideComponents();
            await this.throwDices();
            const popupCloseTCS = new PromiseCompletionSource<void>();
            this.gameController.showDiceResultPopup(this.gameModel.action.value as DiceRollAction, popupCloseTCS);
            await this.collectDices();
            await popupCloseTCS.result;
            this.showComponents();
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            this.inputBlocker.blocked = false;
        }
    }

    private async onDiceRollsOver(): Promise<boolean> {
        if (this.playerModel.rolls.value === 0) {
            await this.gameController.showDiceRollsOfferPopup(PersonalOfferReason.RED_BUTTON);
            return true;
        }
        return false;
    }

    private async throwDices(): Promise<void> {
        this.throwDiceFlag = true;
        this.soundResources.assets_sounds_Roll_09_mp3.play();
        const diceRollAction = this.gameModel.action.value as DiceRollAction;
        await this.animatedDices.throwDices(diceRollAction.diceResults);
    }

    private async collectDices(): Promise<void> {
        await this.animatedDices.collectDices();
    }

    private async onClaimHourlyBonusRewardClick(): Promise<void> {
        const fullscreenInputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(fullscreenInputBlocker);
        this.asyncOperationNotification.show();
        try {
            this.gameController.popupShown();
            const reward = await this.fortuneWheelController.getClaimHourlyBonusReward();
            this.asyncOperationNotification.hide();
            await this.diceCollectAnimation.startAnimation();
            this.playerModel.addRolls(reward);
            await this.fortuneWheelModel.updateFortuneWheelStatus();
        } catch (e: unknown) {
            this.logger.error("Exception on claim hourly bonus nft reward: ", e);
        }
        finally {
            this.gameController.popupHidden();
            this.asyncOperationNotification.hide();
            this.removeChild(fullscreenInputBlocker);
        }
    }

    private async onHourlyBonusSceneRequested(state: HourlyBonusState): Promise<void> {
        await this.inputBlockerOperation(this.gameController.showHourlyBonusPopup(state));
    }

    private async onFortuneWheelPropertyChanged(model: IFortuneWheelModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "spinAvailableIn"):
                const currentSection = model.currentHourlyBonusSection.value;
                if (!isNaN(currentSection) && currentSection === 3 &&
                    this.firstTimeExperienceModel.tooltipsInfo.value?.hourlyBonusWheelIntro &&
                    this.fortuneWheelModel.spinAvailableIn.value == 0
                ) {
                    await this.operationAfterAllPopupsClose(
                        () => this.gameController.showCheckoutHourlyBonusPopup(true),
                        () => !isNaN(currentSection) && currentSection === 3 &&
                            this.firstTimeExperienceModel.tooltipsInfo?.value.hourlyBonusWheelIntro &&
                            this.gameViewModel.popupsShown.value == 0 &&
                            this.fortuneWheelModel.spinAvailableIn.value == 0
                    );
                }
                break;
        }
    }

    private async inputBlockerOperation<TResult>(operation: Promise<TResult>): Promise<TResult> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        try {
            return await operation;
        }
        catch (e: unknown) {
            throw e;
        }
        finally {
            this.removeChild(inputBlocker);
        }
    }

    protected layout(width: number, height: number): void {
        this.background.width = width;
        this.background.height = height;
        this.cityAnimation?.position.set(width * 0.5, height * 0.5);
        this.myBuildingsFadeScreen.width = width;
        this.myBuildingsFadeScreen.height = height;
        this.topPanel.position.x = 25;
        this.bottomPanel.position.set(width * 0.5 - 1, 1391);
        this.rollProgress.position.set(width * 0.5, 1695);

        this.rollButton.position.set(265, 1340);
        this.myBuildingsButton.position.set(40, 1558);
        this.marketplaceButton.position.set(616, 1558);
        this.hand.scale.set(HandWithDelay.DEFAULT_SCALE);
        this.hand.position.set(this.rollButton.x + this.rollButton.getLocalBounds().width * 0.85, this.rollButton.y - this.rollButton.getLocalBounds().height * 0.45);
        this.newsPresenter.width = width;
        this.newsPresenter.position.set(0, 0);
        this.inputBlocker.width = width;
        this.inputBlocker.height = height;
        // Setup Camera
        const camera = Camera.main;
        camera.position.set(-0.63, 1.1, 0.63);
        camera.rotationQuaternion = Quaternion.fromEuler(55, 135, 0);
        // Turn global sunLight to correspond to video
        this.shadowCastingLight.light.rotationQuaternion = Quaternion.fromEuler(85, -105, 0);
        // Adjust shadow settings
        this.shadowCastingLight.shadowArea = 2;
        this.shadowCastingLight.softness = 0;

        this.layoutFeaturePanels(width, height);

        this.diceCollectAnimation.position.set(this.rightPanel.position.x + this.hourlyBonusButton.width * 0.5, this.rightPanel.position.y + this.hourlyBonusButton.y + this.hourlyBonusButton.height * 0.5);
        this.collectAnimationPoint.set(this.rollProgress.position.x - this.diceCollectAnimation.position.x, this.rollProgress.position.y - this.diceCollectAnimation.position.y);
    }

    private layoutFeaturePanels(_width: number, _height: number): void {
        this.leftPanel.alignmentStrategy();
        this.leftPanel.position.set(15, utils.isMobile.any ? 215 : 175);
        this.rightPanel.alignmentStrategy();
        this.rightPanel.position.set(651, utils.isMobile.any ? 215 : 175);
    }

    private floatingTopPanel(): void {
        const initialPositionY = this.toLocal(TopPanel.GLOBAL_TOP_PANEL_POSITION).y + 20;
        this.topPanel.position.y = initialPositionY;
        this.topPanel.initialPositionY = initialPositionY;
        this.layoutFeaturePanels(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.gameModel.propertyChanged.remove(this.onGameModelPropertyChanged, this);
        this.playerModel.propertyChanged.remove(this.onPlayerModelPropertyChanged, this);
        this.background.off("pointerdown", this.gameController.userActive, this.gameController);
        this.tempBackground.off("pointerdown", this.gameController.userActive, this.gameController);
        this.cityAnimation?.off("pointerdown", this.gameController.userActive, this.gameController);
        this.renterButton.onButtonClick.remove(this.onRenterInfoButtonClick, this);
        this.rollButton.onUp.remove(this.onRollClick, this);
        this.tournamentButton.onButtonClick.remove(this.onTournamentButtonClick, this);
        this.tournamentModel.propertyChanged.remove(this.onTournamentModelPropertyChanged, this);
        this.renterPolicyModel.propertyChanged.remove(this.onRenterPolicyModelPropertyChanged, this);
        this.firstTimeExperienceModel.propertyChanged.remove(this.onFirstTimeExperienceModelChanged, this);
        this.firstTimeExperienceModel.tooltipsInfo.value.propertyChanged.remove(this.onFirstTimeExperienceTooltipsChanged, this);
        this.diceCollectAnimation.fakeDiceCollected.remove(this.rollProgress.incrementProgress, this.rollProgress);
        this.myBuildingsButton.onUp.remove(this.myBuildingsClickHandle, this);
        this.marketplaceButton.onUp.remove(this.marketplaceClickHandle, this);

        this.hourlyBonusButton.onStyleChanged.remove(this.rightPanel.alignmentStrategy, this.rightPanel);
        this.hourlyBonusButton.onClaimHourlyBonusRewardClick.remove(this.onClaimHourlyBonusRewardClick, this);
        this.hourlyBonusButton.onHourlyBonusSceneRequested.remove(this.onHourlyBonusSceneRequested, this);
        this.fortuneWheelModel.propertyChanged.remove(this.onFortuneWheelPropertyChanged, this);
        this.latestNewsModel.propertyChanged.remove(this.onLatestNewsModelPropertyChange, this);

        this.showComponentsAnimation?.kill();
        this.removeChild(this.topPanel);
        super.destroy(options);
    }

    private setupStage3d(): void {
        // Setup IBL
        const color = "999999";
        const nightColor = "7b7e8f";
        const dayColor = "656d99";
        const ibl = new ImageBasedLighting(
            Cubemap.fromColors(
                Color.fromHex(color),
                Color.fromHex(color),
                Color.fromHex(color),
                Color.fromHex(color),
                Color.fromHex(color),
                Color.fromHex(color)
            ),
            Cubemap.fromColors(
                Color.fromHex(nightColor),
                Color.fromHex(nightColor),
                Color.fromHex("aaaaaa"),
                Color.fromHex(nightColor),
                Color.fromHex(nightColor),
                Color.fromHex(dayColor))
        );

        LightingEnvironment.main = new LightingEnvironment(this.renderer, ibl);

        // Setup Lights
        const sunLight = new Light();
        sunLight.type = LightType.directional;
        sunLight.intensity = 3;
        sunLight.color = Color.fromHex("838ec7");
        sunLight.rotationQuaternion.setEulerAngles(85, 85, 0);
        LightingEnvironment.main.lights.push(sunLight);

        const specularLight = new Light();
        specularLight.type = LightType.directional;
        specularLight.intensity = 10;
        specularLight.color = Color.fromHex("ffffff");
        specularLight.rotationQuaternion.setEulerAngles(45, 135, 0);
        LightingEnvironment.main.lights.push(specularLight);

        // Setup Shadows
        this.shadowCastingLight = new ShadowCastingLight(this.renderer, sunLight, {
            shadowTextureSize: 2048,
            quality: ShadowQuality.medium
        });
        this.shadowCastingLight.softness = 0;
        this.shadowCastingLight.shadowArea = 2;
    }
}
